'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ChevronLeft, ArrowRight, Upload, X, FileText, User, Phone, Mail, Building2 } from 'lucide-react';
import type { BookingData } from './BookingWizard';

type PatientForm = {
  patientName: string;
  patientAge: string;
  patientGender: string;
  patientPhone: string;
  patientEmail: string;
  corporateCode: string;
};

export default function Step3PatientDetails({
  bookingData,
  updateBooking,
  onNext,
  onBack,
}: {
  bookingData: BookingData;
  updateBooking: (u: Partial<BookingData>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PatientForm>({
    defaultValues: {
      patientName: bookingData.patientName,
      patientAge: bookingData.patientAge,
      patientGender: bookingData.patientGender,
      patientPhone: bookingData.patientPhone,
      patientEmail: bookingData.patientEmail,
      corporateCode: bookingData.corporateCode,
    },
  });

  const onSubmit = (data: PatientForm) => {
    updateBooking({ ...data });
    onNext();
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setFileName(file.name);
      updateBooking({ prescriptionFile: file.name });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      updateBooking({ prescriptionFile: file.name });
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <h2 className="font-bold text-xl text-foreground">Patient Details</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Fill in patient information. All fields marked * are required.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6">
          {/* Patient info */}
          <div>
            <h3 className="font-semibold text-sm text-foreground mb-4 flex items-center gap-2">
              <User size={15} className="text-primary" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Full Name <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">As per Aadhaar / government ID</p>
                <input
                  type="text"
                  placeholder="e.g. Ramesh Kumar Sharma"
                  className={`input-field ${errors.patientName ? 'error' : ''}`}
                  {...register('patientName', {
                    required: 'Full name is required',
                    minLength: { value: 3, message: 'Name must be at least 3 characters' },
                  })}
                />
                {errors.patientName && (
                  <p className="text-xs text-danger mt-1.5">{errors.patientName.message}</p>
                )}
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Age <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  placeholder="e.g. 45"
                  min="1"
                  max="120"
                  className={`input-field ${errors.patientAge ? 'error' : ''}`}
                  {...register('patientAge', {
                    required: 'Age is required',
                    min: { value: 1, message: 'Age must be at least 1' },
                    max: { value: 120, message: 'Please enter a valid age' },
                  })}
                />
                {errors.patientAge && (
                  <p className="text-xs text-danger mt-1.5">{errors.patientAge.message}</p>
                )}
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Gender <span className="text-danger">*</span>
                </label>
                <select
                  className={`input-field ${errors.patientGender ? 'error' : ''}`}
                  {...register('patientGender', { required: 'Please select gender' })}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.patientGender && (
                  <p className="text-xs text-danger mt-1.5">{errors.patientGender.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact details */}
          <div>
            <h3 className="font-semibold text-sm text-foreground mb-4 flex items-center gap-2">
              <Phone size={15} className="text-primary" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Mobile Number <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">Reports will be sent on WhatsApp</p>
                <div className="flex">
                  <span className="flex items-center px-3 bg-muted border border-r-0 border-border rounded-l-lg text-sm text-muted-foreground font-medium">
                    +91
                  </span>
                  <input
                    type="tel"
                    placeholder="98765 43210"
                    className={`input-field rounded-l-none border-l-0 ${errors.patientPhone ? 'error' : ''}`}
                    {...register('patientPhone', {
                      required: 'Mobile number is required',
                      pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit mobile number' },
                    })}
                  />
                </div>
                {errors.patientPhone && (
                  <p className="text-xs text-danger mt-1.5">{errors.patientPhone.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Email Address
                </label>
                <p className="text-xs text-muted-foreground mb-2">PDF report will be emailed here</p>
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    placeholder="ramesh@email.com"
                    className={`input-field pl-10 ${errors.patientEmail ? 'error' : ''}`}
                    {...register('patientEmail', {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Enter a valid email address',
                      },
                    })}
                  />
                </div>
                {errors.patientEmail && (
                  <p className="text-xs text-danger mt-1.5">{errors.patientEmail.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Corporate code */}
          <div>
            <h3 className="font-semibold text-sm text-foreground mb-4 flex items-center gap-2">
              <Building2 size={15} className="text-primary" />
              Corporate Employee (Optional)
            </h3>
            <div className="max-w-sm">
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Corporate / Employee Code
              </label>
              <p className="text-xs text-muted-foreground mb-2">
                Enter your company code if your employer has a corporate tie-up with 51 Plus Lab
              </p>
              <input
                type="text"
                placeholder="e.g. CORP-TCS-0042"
                className="input-field"
                {...register('corporateCode')}
              />
            </div>
          </div>

          {/* Prescription upload */}
          <div>
            <h3 className="font-semibold text-sm text-foreground mb-4 flex items-center gap-2">
              <FileText size={15} className="text-primary" />
              Upload Prescription (Optional)
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              If you have a doctor&apos;s prescription, upload it here. Accepted formats: PDF, JPG, PNG (max 5 MB)
            </p>

            {fileName ? (
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                <FileText size={18} className="text-green-600 flex-shrink-0" />
                <span className="text-sm font-medium text-green-800 flex-1 truncate">{fileName}</span>
                <button
                  type="button"
                  onClick={() => {
                    setFileName('');
                    updateBooking({ prescriptionFile: '' });
                  }}
                  className="text-green-600 hover:text-green-800 transition-colors"
                  aria-label="Remove file"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragOver ? 'border-primary bg-secondary' : 'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
              >
                <Upload size={28} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground mb-1">Drag & drop your prescription here</p>
                <p className="text-xs text-muted-foreground mb-4">or</p>
                <label className="btn-outline py-2 px-4 text-sm cursor-pointer">
                  Browse Files
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
                <p className="text-xs text-muted-foreground mt-3">
                  You can also send prescription via WhatsApp: +91 12345 67890
                </p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="pt-4 border-t border-border flex items-center justify-between">
            <button type="button" onClick={onBack} className="btn-outline py-2.5 px-5 text-sm flex items-center gap-2">
              <ChevronLeft size={15} />
              Back
            </button>
            <button type="submit" className="btn-primary">
              Review & Confirm
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}